import { useState, useEffect, useCallback, useRef } from 'react';

export interface DeviceInfo {
  deviceId: string;
  label: string;
  kind: 'videoinput' | 'audioinput' | 'audiooutput';
}

export interface DeviceSelection {
  videoDeviceId?: string;
  audioDeviceId?: string;
  audioOutputDeviceId?: string;
}

export interface DeviceTestResult {
  success: boolean;
  error?: string;
  volume?: number;
  duration?: number;
}

export interface UseDeviceEnumerationReturn {
  devices: {
    videoInputs: DeviceInfo[];
    audioInputs: DeviceInfo[];
    audioOutputs: DeviceInfo[];
  };
  selectedDevices: DeviceSelection;
  isLoading: boolean;
  error: string | null;
  hasPermission: boolean;
  isTestingMicrophone: boolean;
  isTestingSpeaker: boolean;
  enumerateDevices: () => Promise<void>;
  refreshDevices: () => Promise<void>;
  selectVideoDevice: (deviceId: string) => void;
  selectAudioDevice: (deviceId: string) => void;
  selectAudioOutputDevice: (deviceId: string) => void;
  applySelectedAudioOutput: (audioElement: HTMLAudioElement) => Promise<boolean>;
  testMicrophone: (deviceId?: string) => Promise<DeviceTestResult>;
  testSpeaker: (deviceId?: string) => Promise<DeviceTestResult>;
  clearError: () => void;
}

const DEVICE_STORAGE_KEY = 'voxi-device-preferences';

export const useDeviceEnumeration = (): UseDeviceEnumerationReturn => {
  const [devices, setDevices] = useState<{
    videoInputs: DeviceInfo[];
    audioInputs: DeviceInfo[];
    audioOutputs: DeviceInfo[];
  }>({
    videoInputs: [],
    audioInputs: [],
    audioOutputs: [],
  });

  const [selectedDevices, setSelectedDevices] = useState<DeviceSelection>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasPermission, setHasPermission] = useState(false);
  const [isTestingMicrophone, setIsTestingMicrophone] = useState(false);
  const [isTestingSpeaker, setIsTestingSpeaker] = useState(false);
  
  const lastEnumerationRef = useRef<number>(0);

  // Load saved device preferences on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(DEVICE_STORAGE_KEY);
      if (saved) {
        const preferences = JSON.parse(saved);
        setSelectedDevices(preferences);
      }
    } catch (error) {
      console.warn('Failed to load device preferences:', error);
    }
  }, []);

  // Save device preferences when selection changes
  const saveDevicePreferences = useCallback((selection: DeviceSelection) => {
    try {
      localStorage.setItem(DEVICE_STORAGE_KEY, JSON.stringify(selection));
    } catch (error) {
      console.warn('Failed to save device preferences:', error);
    }
  }, []);

  // Check if browser supports setSinkId
  const supportsSetSinkId = useCallback(() => {
    return 'setSinkId' in HTMLMediaElement.prototype;
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const enumerateDevices = useCallback(async (forceRefresh = false, requestPermissions = true) => {
    // Prevent rapid repeated calls unless forced
    const now = Date.now();
    if (!forceRefresh && now - lastEnumerationRef.current < 1000) {
      console.log('🎤 Device enumeration skipped - too recent');
      return;
    }
    lastEnumerationRef.current = now;

    console.log('🎤 Device enumeration starting - forceRefresh:', forceRefresh, 'requestPermissions:', requestPermissions);
    setIsLoading(true);
    setError(null);

    try {
      // Check for basic media permissions only if requested
      let stream: MediaStream | null = null;
      if (requestPermissions) {
        try {
          stream = await navigator.mediaDevices.getUserMedia({ 
            video: true, 
            audio: true 
          });
          setHasPermission(true);
        } catch (permError) {
          console.warn('Media permissions not granted:', permError);
          setHasPermission(false);
        }
      }

      // Enumerate devices
      const deviceList = await navigator.mediaDevices.enumerateDevices();
      
      const videoInputs: DeviceInfo[] = [];
      const audioInputs: DeviceInfo[] = [];
      const audioOutputs: DeviceInfo[] = [];

      deviceList.forEach((device) => {
        // Always include devices, even without labels (provide generic names)
        let deviceLabel = device.label;
        if (!deviceLabel) {
          // Generate user-friendly generic names when permissions aren't granted
          const kindCounts = {
            videoinput: videoInputs.length + 1,
            audioinput: audioInputs.length + 1, 
            audiooutput: audioOutputs.length + 1
          };
          
          const kindLabels = {
            videoinput: `Camera ${kindCounts.videoinput}`,
            audioinput: `Microphone ${kindCounts.audioinput}`,
            audiooutput: `Speaker ${kindCounts.audiooutput}`
          };
          
          deviceLabel = kindLabels[device.kind as keyof typeof kindLabels] || 
                       `${device.kind} (${device.deviceId.slice(0, 8)}...)`;
        }

        const deviceInfo: DeviceInfo = {
          deviceId: device.deviceId,
          label: deviceLabel,
          kind: device.kind as DeviceInfo['kind'],
        };

        switch (device.kind) {
          case 'videoinput':
            videoInputs.push(deviceInfo);
            break;
          case 'audioinput':
            audioInputs.push(deviceInfo);
            break;
          case 'audiooutput':
            // Only include audio outputs if setSinkId is supported
            if (supportsSetSinkId()) {
              audioOutputs.push(deviceInfo);
            }
            break;
        }
      });

      console.log('🎤 Devices enumerated:', { 
        videoInputs: videoInputs.length, 
        audioInputs: audioInputs.length, 
        audioOutputs: audioOutputs.length 
      });
      
      setDevices({
        videoInputs,
        audioInputs,
        audioOutputs,
      });

      // A saved device that is gone (unplugged, or its id changed since it was saved) fails
      // getUserMedia's exact deviceId on every try. Without permission the ids are blank: keep it.
      const isAvailable = (deviceId: string | undefined, list: DeviceInfo[]) =>
        !!deviceId && (list.every(d => !d.deviceId) || list.some(d => d.deviceId === deviceId));

      // Auto-select first device of each type if none (or a missing one) is selected
      setSelectedDevices(prev => {
        const updated = { ...prev };
        let hasChanges = false;

        if (!isAvailable(prev.videoDeviceId, videoInputs) && videoInputs.length > 0) {
          updated.videoDeviceId = videoInputs[0].deviceId;
          hasChanges = true;
        }

        if (!isAvailable(prev.audioDeviceId, audioInputs) && audioInputs.length > 0) {
          updated.audioDeviceId = audioInputs[0].deviceId;
          hasChanges = true;
        }

        if (!isAvailable(prev.audioOutputDeviceId, audioOutputs) && audioOutputs.length > 0) {
          updated.audioOutputDeviceId = audioOutputs[0].deviceId;
          hasChanges = true;
        }

        if (hasChanges) {
          saveDevicePreferences(updated);
        }

        return updated;
      });

      // Clean up stream
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }

    } catch (error) {
      console.error('Failed to enumerate devices:', error);
      setError('Failed to enumerate media devices. Please check your permissions.');
    } finally {
      setIsLoading(false);
    }
  }, [hasPermission, saveDevicePreferences, supportsSetSinkId]);

  // Force refresh devices - bypasses debouncing for manual refresh
  const refreshDevices = useCallback(async () => {
    console.log('🔄 Manual device refresh triggered');
    await enumerateDevices(true, true); // Force refresh and request permissions
  }, [enumerateDevices]);

  const selectVideoDevice = useCallback((deviceId: string) => {
    setSelectedDevices(prev => {
      const updated = { ...prev, videoDeviceId: deviceId };
      saveDevicePreferences(updated);
      return updated;
    });
  }, [saveDevicePreferences]);

  const selectAudioDevice = useCallback((deviceId: string) => {
    setSelectedDevices(prev => {
      const updated = { ...prev, audioDeviceId: deviceId };
      saveDevicePreferences(updated);
      return updated;
    });
  }, [saveDevicePreferences]);

  const selectAudioOutputDevice = useCallback((deviceId: string) => {
    setSelectedDevices(prev => {
      const updated = { ...prev, audioOutputDeviceId: deviceId };
      saveDevicePreferences(updated);
      return updated;
    });
  }, [saveDevicePreferences]);

  const applySelectedAudioOutput = useCallback(async (audioElement: HTMLAudioElement): Promise<boolean> => {
    if (!selectedDevices.audioOutputDeviceId || !supportsSetSinkId()) {
      return false;
    }

    try {
      // @ts-ignore - setSinkId exists but TypeScript doesn't know about it
      await audioElement.setSinkId(selectedDevices.audioOutputDeviceId);
      return true;
    } catch (error) {
      console.error('Failed to set audio output device:', error);
      return false;
    }
  }, [selectedDevices.audioOutputDeviceId, supportsSetSinkId]);

  const testMicrophone = useCallback(async (deviceId?: string): Promise<DeviceTestResult> => {
    if (isTestingMicrophone) {
      return { success: false, error: 'Test already in progress' };
    }

    const testDeviceId = deviceId || selectedDevices.audioDeviceId;
    if (!testDeviceId) {
      return { success: false, error: 'No microphone device selected' };
    }

    setIsTestingMicrophone(true);
    
    let stream: MediaStream | null = null;
    try {
      // Get microphone stream with specific device
      stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          deviceId: { exact: testDeviceId },
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
        }
      });

      // Create audio context to analyze volume
      const audioContext = new AudioContext();
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      
      source.connect(analyser);
      analyser.fftSize = 256;
      
      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);
      
      let maxVolume = 0;
      let samples = 0;
      const sampleDuration = 2000; // 2 seconds
      const startTime = Date.now();
      
      return new Promise((resolve) => {
        const checkVolume = () => {
          analyser.getByteFrequencyData(dataArray);
          
          // Calculate volume
          let sum = 0;
          for (let i = 0; i < bufferLength; i++) {
            sum += dataArray[i];
          }
          const avgVolume = sum / bufferLength;
          maxVolume = Math.max(maxVolume, avgVolume);
          samples++;
          
          if (Date.now() - startTime < sampleDuration) {
            requestAnimationFrame(checkVolume);
          } else {
            // Clean up
            stream?.getTracks().forEach(track => track.stop());
            audioContext.close();
            setIsTestingMicrophone(false);
            
            const success = maxVolume > 5; // Threshold for detecting audio input
            resolve({
              success,
              volume: maxVolume,
              duration: sampleDuration,
              error: success ? undefined : 'No audio input detected. Please speak into your microphone.'
            });
          }
        };
        
        checkVolume();
      });
      
    } catch (error) {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
      setIsTestingMicrophone(false);
      
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to access microphone'
      };
    }
  }, [selectedDevices.audioDeviceId, isTestingMicrophone]);

  const testSpeaker = useCallback(async (deviceId?: string): Promise<DeviceTestResult> => {
    if (isTestingSpeaker) {
      return { success: false, error: 'Test already in progress' };
    }

    const testDeviceId = deviceId || selectedDevices.audioOutputDeviceId;
    if (!testDeviceId || !supportsSetSinkId()) {
      return { success: false, error: 'Speaker testing not supported or no device selected' };
    }

    setIsTestingSpeaker(true);
    
    try {
      // Create Web Audio API context and generate test tone
      const audioContext = new AudioContext();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      // Create a MediaStreamDestination to capture the audio
      const destination = audioContext.createMediaStreamDestination();
      
      oscillator.connect(gainNode);
      gainNode.connect(destination);
      
      // Create a pleasant two-tone chime (C5 and G5 - perfect fifth interval)
      oscillator.frequency.value = 523; // C5 note - pleasant and audible
      oscillator.type = 'sine'; // Smooth sine wave for pleasant sound
      
      // Create second oscillator for harmony
      const oscillator2 = audioContext.createOscillator();
      const gainNode2 = audioContext.createGain();
      oscillator2.frequency.value = 784; // G5 note - creates a pleasant chord
      oscillator2.type = 'sine';
      
      oscillator2.connect(gainNode2);
      gainNode2.connect(destination);
      
      // Set pleasant, moderate volume levels
      gainNode.gain.setValueAtTime(0.15, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.8);
      
      gainNode2.gain.setValueAtTime(0.1, audioContext.currentTime);
      gainNode2.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.8);
      
      // Create audio element and set the source to our generated stream
      const audio = new Audio();
      audio.srcObject = destination.stream;
      audio.volume = 0.6; // Moderate, pleasant volume
      
      // Set the audio output device using setSinkId
      // @ts-ignore - setSinkId exists but TypeScript doesn't know about it
      await audio.setSinkId(testDeviceId);
      
      // Start both oscillators for the pleasant chime
      oscillator.start();
      oscillator.stop(audioContext.currentTime + 0.8);
      oscillator2.start();
      oscillator2.stop(audioContext.currentTime + 0.8);
      
      // Play the audio through the selected device
      await audio.play();
      
      return new Promise((resolve) => {
        setTimeout(() => {
          audio.pause();
          audio.srcObject = null;
          audioContext.close();
          setIsTestingSpeaker(false);
          resolve({
            success: true,
            duration: 1000
          });
        }, 1200); // Wait a bit longer than the tone duration
      });
      
    } catch (error) {
      setIsTestingSpeaker(false);
      
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to test speaker'
      };
    }
  }, [selectedDevices.audioOutputDeviceId, isTestingSpeaker, supportsSetSinkId]);

  // Listen for device changes
  useEffect(() => {
    const handleDeviceChange = () => {
      console.log('Device change detected, re-enumerating...');
      enumerateDevices(false, true); // Don't force refresh but request permissions for accurate labels
    };

    navigator.mediaDevices?.addEventListener('devicechange', handleDeviceChange);

    return () => {
      navigator.mediaDevices?.removeEventListener('devicechange', handleDeviceChange);
    };
  }, [enumerateDevices]);

  // Initial enumeration - request permissions to get real device names
  useEffect(() => {
    console.log('🎤 Initial device enumeration starting...');
    enumerateDevices(false, true); // Don't force refresh, but DO request permissions
  }, [enumerateDevices]);

  return {
    devices,
    selectedDevices,
    isLoading,
    error,
    hasPermission,
    isTestingMicrophone,
    isTestingSpeaker,
    enumerateDevices,
    refreshDevices,
    selectVideoDevice,
    selectAudioDevice,
    selectAudioOutputDevice,
    applySelectedAudioOutput,
    testMicrophone,
    testSpeaker,
    clearError,
  };
};